import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, Clock } from 'lucide-react';

import { useClaudeStatus } from '../hooks/useClaudeStatus';

/**
 * App-wide warning about the Claude credential.
 *
 * Admin-only — the underlying query is disabled for other roles, so this
 * renders nothing for them.
 *
 * Hidden while the credential is healthy. In practice that is almost always:
 * the Claude CLI refreshes its own token on every call and pushes the deadline
 * ~30 days out, so this appears mainly after a long idle period.
 */
export const ClaudeStatusBanner: React.FC = () => {
  const { data: status } = useClaudeStatus();

  if (!status) return null;
  if (status.state === 'active') return null;

  const isExpired = status.state === 'expired' || status.state === 'not_configured';

  const text =
    status.state === 'not_configured'
      ? 'No Claude credential is configured — blog generation will fail.'
      : status.state === 'expired'
      ? 'The Claude credential has expired — blog generation will fail.'
      : `The Claude credential expires ${
          status.days_remaining !== null && status.days_remaining <= 1
            ? 'in less than a day'
            : `in ${Math.floor(status.days_remaining ?? 0)} days`
        }.`;

  const tone = isExpired
    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
    : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-400';

  const linkTone = isExpired
    ? 'text-red-600 dark:text-red-300'
    : 'text-yellow-700 dark:text-yellow-300';

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-center gap-2 px-4 lg:px-6 py-2.5 border-b text-sm ${tone}`}
    >
      {isExpired ? (
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
      ) : (
        <Clock className="h-4 w-4 flex-shrink-0" />
      )}
      <span>{text}</span>
      <Link
        to="/settings"
        className={`ml-auto hover:underline text-xs font-medium ${linkTone}`}
      >
        Fix now
      </Link>
    </motion.div>
  );
};
